"use client";

import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchHomePageData, HomePageSectionData } from "@/api-service/homePage";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "../common/NextImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { ChevronLeft, ChevronRight } from "lucide-react";

import "swiper/css";

import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";
import { CategoryCardSkeleton } from "../common/Skeletons";

interface SingleCategorySectionSliderProps {
  section: HomePageSectionData;
  countryName: string;
}

const SingleCategorySectionSlider: React.FC<SingleCategorySectionSliderProps> = ({
  section,
  countryName,
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const { fastPush } = useFastRouter();

  const handlePrev = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const handleNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  const handleSwiperInit = useCallback((swiper: SwiperType) => {
    setSwiperInstance(swiper);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  if (!section.categories || section.categories.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full group mb-8 md:mb-12 last:mb-0">
      {/* DB-Driven Header Title */}
      <h2
        className={`font-semibold tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} ${HEADER_V2_DESIGN_SYSTEM.typography.size20} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText} mb-6 md:mb-8`}
      >
        {section.title}
        {countryName ? ` in ${countryName}` : ""}
      </h2>

      {/* Swiper Slider Wrapper */}
      <div className="relative w-full px-1 py-1">
        <Swiper
          modules={[Keyboard]}
          spaceBetween={12}
          slidesPerView={1.8}
          keyboard={{ enabled: true }}
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
          breakpoints={{
            480: { slidesPerView: 2.2, spaceBetween: 12 },
            640: { slidesPerView: 2.8, spaceBetween: 12 },
            768: { slidesPerView: 3.4, spaceBetween: 12 },
            1024: { slidesPerView: 4.4, spaceBetween: 12 },
          }}
          className="w-full !overflow-visible md:!overflow-hidden"
        >
          {section.categories.map((cat, index) => (
            <SwiperSlide key={cat.id || index} className="h-auto">
              {/* Aspect-square category card */}
              <div
                onClick={() => cat.slug && fastPush(`/collections/${cat.slug}`)}
                className={`relative w-full aspect-square overflow-hidden cursor-pointer group 
                  ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card} 
                  ${HEADER_V2_DESIGN_SYSTEM.elevation.card} 
                  ${HEADER_V2_DESIGN_SYSTEM.elevation.cardHover} transition-all duration-300`}
              >
                {/* Full-width bottom translucent glass name bar */}
                <div className="absolute bottom-0 left-0 right-0 w-full z-10 pointer-events-none bg-white/45 backdrop-blur-md py-2.5 px-3 text-center border-t border-white/30">
                  <p
                    className={`text-xs md:text-sm font-bold tracking-tight leading-tight text-slate-900 truncate capitalize 
                    ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilySans}`}
                  >
                    {cat.name}
                  </p>
                </div>

                <NextImage
                  src={cat.imageUrl?.trim() || "/assets/site/images/default.png"}
                  alt={
                    cat.name
                      ? `${cat.name}${countryName ? ` in ${countryName}` : ""}`
                      : "Category Image"
                  }
                  fill
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 20vw"
                  className="object-cover transition-transform duration-500 ease-out group-hover:scale-103"
                  priority={index < 5}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Rectangular Translucent Navigation Buttons */}
        {section.categories.length > 2 && (
          <>
            <button
              onClick={(e) => {
                e.preventDefault();
                handlePrev();
              }}
              disabled={isBeginning}
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 
                hidden md:flex items-center justify-center w-8 h-16 rounded-r-md 
                bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                disabled:opacity-30 disabled:pointer-events-none`}
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                handleNext();
              }}
              disabled={isEnd}
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 
                hidden md:flex items-center justify-center w-8 h-16 rounded-l-md 
                bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                disabled:opacity-30 disabled:pointer-events-none`}
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5 stroke-[2.5]" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

interface CategoryWithSliderProps {
  section?: HomePageSectionData;
}

const CategoryWithSlider: React.FC<CategoryWithSliderProps> = ({ section: passedSection }) => {
  const countryName = useResolvedCountryName();
  const [sections, setSections] = useState<HomePageSectionData[]>([]);
  const [loading, setLoading] = useState(!passedSection);

  useEffect(() => {
    if (passedSection) {
      setSections([passedSection]);
      setLoading(false);
      return;
    }

    let isMounted = true;
    const loadHomePageData = async () => {
      try {
        setLoading(true);
        const res = await fetchHomePageData();
        if (isMounted) {
          const allSections = res?.data?.sections || [];
          const categorySecs = allSections.filter(
            (sec) => sec.sectionType?.toUpperCase() === "CATEGORIES"
          );
          setSections(categorySecs);

        }
      } catch (error: unknown) {
        console.error("Error fetching homepage category data:", error);
        if (isMounted) {
          toast.error("Error fetching category sections");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHomePageData();
    return () => {
      isMounted = false;
    };
  }, [passedSection]);

  if (passedSection) {
    if (!passedSection.categories || passedSection.categories.length === 0) {
      return null;
    }
    return (
      <section className={`w-full max-w-7xl mx-auto my-6 md:my-8 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
        <SingleCategorySectionSlider key={passedSection.id} section={passedSection} countryName={countryName} />
      </section>
    );
  }

  if (!loading && sections.length === 0) {
    return null;
  }

  return (
    <section className={`w-full max-w-7xl mx-auto my-6 md:my-8 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
      {loading ? (
        <div className="w-full">
          <div className="h-6 w-44 bg-slate-200 rounded mb-6 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <CategoryCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : (
        sections.map((sec) => (
          <SingleCategorySectionSlider key={sec.id} section={sec} countryName={countryName} />
        ))
      )}
    </section>
  );
};

export default CategoryWithSlider;

